import { useEffect, useRef } from 'react';
import { Loader2, Mic } from 'lucide-react';

function recognitionLang(code: string): string {
  const c = (code || 'en').toLowerCase();
  if (c === 'hi-en' || c.includes('hinglish')) return 'en-IN';
  if (c === 'hi-in' || (c.startsWith('hi') && !c.includes('hinglish'))) return 'hi-IN';
  if (c.startsWith('kn')) return 'kn-IN';
  if (c.startsWith('mr')) return 'mr-IN';
  if (c.startsWith('ru')) return 'ru-RU';
  if (c.startsWith('pt')) return 'pt-BR';
  if (c.startsWith('es')) return 'es-ES';
  if (c.startsWith('en-in')) return 'en-IN';
  if (c.startsWith('en')) return 'en-US';
  return 'en-US';
}

type Props = {
  disabled: boolean;
  listening: boolean;
  language: string;
  onListeningChange: (v: boolean) => void;
  onTranscript: (text: string) => void;
};

/**
 * Browser speech-to-text (Web Speech API). Chrome / Edge recommended.
 */
export function AiInterviewVoiceMic({
  disabled,
  listening,
  language,
  onListeningChange,
  onTranscript,
}: Props) {
  const recRef = useRef<{ stop: () => void; start: () => void } | null>(null);

  useEffect(() => {
    return () => {
      try {
        recRef.current?.stop();
      } catch {
        /* ignore */
      }
      recRef.current = null;
    };
  }, []);

  const supported =
    typeof window !== 'undefined' &&
    Boolean(
      (window as unknown as { SpeechRecognition?: unknown }).SpeechRecognition ||
        (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition,
    );

  function stop() {
    try {
      recRef.current?.stop();
    } catch {
      /* ignore */
    }
    recRef.current = null;
    onListeningChange(false);
  }

  function start() {
    const W = window as unknown as {
      SpeechRecognition?: new () => {
        lang: string;
        continuous: boolean;
        interimResults: boolean;
        start: () => void;
        stop: () => void;
        onresult: ((ev: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
      webkitSpeechRecognition?: new () => {
        lang: string;
        continuous: boolean;
        interimResults: boolean;
        start: () => void;
        stop: () => void;
        onresult: ((ev: { results: ArrayLike<{ 0: { transcript: string } }> }) => void) | null;
        onend: (() => void) | null;
        onerror: (() => void) | null;
      };
    };
    const SR = W.SpeechRecognition || W.webkitSpeechRecognition;
    if (!SR) return;

    try {
      recRef.current?.stop();
    } catch {
      /* ignore */
    }

    const r = new SR();
    r.lang = recognitionLang(language);
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (ev) => {
      const first = ev.results[0]?.[0];
      const t = typeof first?.transcript === 'string' ? first.transcript.trim() : '';
      if (t) onTranscript(t);
    };
    r.onend = () => {
      recRef.current = null;
      onListeningChange(false);
    };
    r.onerror = () => {
      recRef.current = null;
      onListeningChange(false);
    };
    recRef.current = r;
    onListeningChange(true);
    r.start();
  }

  if (!supported) {
    return (
      <p className="text-xs text-amber-200/90 text-center rounded-xl border border-amber-500/30 bg-amber-950/25 px-3 py-2">
        Voice input needs a Chromium-based browser (Chrome or Edge). Microphone permission is required.
      </p>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-1.5">
      <button
        type="button"
        disabled={disabled && !listening}
        onClick={() => (listening ? stop() : start())}
        className={[
          'group relative flex h-12 w-12 items-center justify-center rounded-full sm:h-14 sm:w-14',
          'border border-slate-600/90 bg-slate-800 text-sky-100 transition-all',
          'shadow-md shadow-black/25 ring-1 ring-sky-500/25 hover:ring-sky-400/35 hover:bg-slate-700/90 hover:scale-[1.02] active:scale-[0.98]',
          disabled && !listening ? 'pointer-events-none opacity-40' : '',
        ].join(' ')}
        aria-pressed={listening}
        aria-label={listening ? 'Stop listening' : 'Speak your answer'}
      >
        {listening ? (
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        ) : (
          <Mic className="h-6 w-6" strokeWidth={2} aria-hidden />
        )}
        {listening && (
          <span
            className="absolute inset-0 rounded-full border-2 border-sky-400/50 animate-ping pointer-events-none"
            aria-hidden
          />
        )}
      </button>
      <p className="max-w-xs text-center text-[11px] leading-snug text-slate-600 dark:text-slate-400">
        {listening ? 'Listening… speak naturally (tap again to cancel)' : 'Tap the mic when you are ready to respond'}
      </p>
    </div>
  );
}
